import React, { useEffect, useMemo, useState } from 'react';
import './TargetMenu.scss';

interface MenuOptionData {
  id: string;
  label: string;
  // Section the entry belongs to. Entries without one are listed first, under
  // no heading.
  group?: string;
  // Destructive or coercive action, drawn in the warning colour.
  danger?: boolean;
}

interface MenuGroup {
  name: string;
  options: MenuOptionData[];
}

// Ignore any click during the first moments after the menu opens - browser
// focus flaps while it's toggled on, and a stray held click can land on an
// option and silently pick it (same guard as CitySelection).
const CLICK_GRACE_MS = 250;

const TargetMenu: React.FC = () => {
  const [options, setOptions] = useState<MenuOptionData[]>([]);
  const [visible, setVisible] = useState(false);
  const openedAtRef = React.useRef(0);

  useEffect(() => {
    const showHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { options: MenuOptionData[] };
      setOptions(detail.options ?? []);
      setVisible(true);
      openedAtRef.current = Date.now();
    };
    const hideHandler = () => setVisible(false);

    window.addEventListener('skymp5-client:showTargetMenu', showHandler);
    window.addEventListener('skymp5-client:hideTargetMenu', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showTargetMenu', showHandler);
      window.removeEventListener('skymp5-client:hideTargetMenu', hideHandler);
    };
  }, []);

  // Groups in order of first appearance, so whoever builds the list decides
  // the order by the order it lists the entries.
  const groups = useMemo<MenuGroup[]>(() => {
    const byName = new Map<string, MenuGroup>();
    for (const opt of options) {
      const name = opt.group ?? '';
      let g = byName.get(name);
      if (!g) {
        g = { name, options: [] };
        byName.set(name, g);
      }
      g.options.push(opt);
    }
    return [...byName.values()];
  }, [options]);

  const choose = (id: string, e: React.MouseEvent) => {
    // e.detail === 0 means the "click" came from the keyboard (Enter/Space on
    // the DOM-focused button), not a real pointer click.
    if (e.detail === 0) return;
    if (Date.now() - openedAtRef.current < CLICK_GRACE_MS) return;

    setVisible(false);
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage('target-menu:' + id);
    }
  };

  if (!visible || options.length === 0) return null;

  const showHeadings = groups.some((g) => g.name !== '');

  return (
    <div className="target-menu-overlay">
      <div className="target-menu-panel" role="menu" aria-label="Actions">
        <div className="target-menu-title">Actions</div>

        <div className="target-menu-scroll">
          {groups.map((g) => (
            <section className="target-menu-group" key={g.name || 'default'}>
              {showHeadings && g.name && <h3 className="target-menu-group__title">{g.name}</h3>}
              {g.options.map((opt) => (
                <button
                  key={opt.id}
                  role="menuitem"
                  className={'target-menu-item' + (opt.danger ? ' target-menu-item--danger' : '')}
                  onClick={(e) => choose(opt.id, e)}
                  type="button"
                >
                  {opt.label}
                </button>
              ))}
            </section>
          ))}
        </div>

        <div className="target-menu-footer">
          <kbd className="target-menu-kbd">Échap</kbd> pour fermer
        </div>
      </div>
    </div>
  );
};

export default TargetMenu;
